import { useEffect, useRef, useState } from 'react'

import { GRTableColumn } from '../../../components/GRTable/GRTable'

/**
 * Carry `GRTable`'s interaction state across a rebuild of the column
 * definitions.
 *
 * `GRTable` keeps the active sort and each measured column width *on the
 * column objects* and pushes them back up through `onColumnsUpdated`, so a
 * fresh set of definitions would otherwise drop both. Losing a measured width
 * only costs a re-measure; losing the sort visibly reorders the table under
 * the user, which is what happened whenever a table rebuilt its columns in
 * response to an unrelated interaction (ticking a compare checkbox, expanding
 * a row).
 *
 * The sort is taken as a whole rather than per column: `GRTable` clears every
 * other column's `sortOrder` when one is sorted, so mixing a carried-over sort
 * with a definition's static default would leave two columns claiming to be
 * the sort column.
 */
const withPreservedColumnState = <RowType, CustomPropsType, ColumnIdType>(
  next: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[],
  previous: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
): GRTableColumn<RowType, CustomPropsType, ColumnIdType>[] => {
  // Columns are matched positionally, which only holds while the shape of the
  // table is unchanged. A different column count means a genuinely different
  // table (a column was added or removed), so start from the new definitions.
  if (next.length !== previous.length) return next

  return next.map((column, index) => {
    const prior = previous[index]
    return {
      ...column,
      sortOrder: prior.sortOrder,
      width: column.width ?? prior.width,
      ...(column.columns && prior.columns ? { columns: withPreservedColumnState(column.columns, prior.columns) } : {}),
    }
  })
}

/**
 * Mirrors the common `GRTable` controlled-columns pattern:
 *
 *   const [columns, setColumns] = useState(defaultColumns)
 *   useEffect(() => setColumns(defaultColumns), [defaultColumns])
 *
 * Returns `[columns, setColumns]` and re-syncs whenever the memoised
 * `defaultColumns` identity changes (a translation change, a new cell
 * closure), while preserving the sort and measured widths the table itself
 * owns — see {@link withPreservedColumnState}.
 */
export function useControlledColumns<RowType, CustomPropsType = undefined, ColumnIdType = void>(
  defaultColumns: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
): [GRTableColumn<RowType, CustomPropsType, ColumnIdType>[], React.Dispatch<React.SetStateAction<GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]>>] {
  const [columns, setColumns] = useState(defaultColumns)

  // The re-sync must not re-run when the table pushes its own updates back, so
  // the current columns are read from a ref rather than an effect dependency.
  const columnsRef = useRef(columns)
  columnsRef.current = columns

  const isInitialRef = useRef(true)
  useEffect(() => {
    if (isInitialRef.current) {
      isInitialRef.current = false
      return
    }
    setColumns(withPreservedColumnState(defaultColumns, columnsRef.current))
  }, [defaultColumns])

  return [columns, setColumns]
}
